import { computed } from 'vue'
import type { Meeting, MeetingStatus } from '@/types'
import type { MeetingsState } from './state'

export const createGetters = (state: MeetingsState) => {
  const filteredMeetings = computed(() => {
    let result = [...state.meetings]

    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(search) ||
          m.description?.toLowerCase().includes(search)
      )
    }

    if (state.filters.projectId) {
      result = result.filter((m) => m.projectId === state.filters.projectId)
    }

    if (state.filters.status) {
      result = result.filter((m) => m.status === state.filters.status)
    }

    if (state.filters.meetingType) {
      result = result.filter((m) => m.meetingType === state.filters.meetingType)
    }

    return result
  })

  const meetingsByStatus = computed(() => {
    const grouped: Record<MeetingStatus, Meeting[]> = {
      scheduled: [],
      in_progress: [],
      completed: [],
      cancelled: [],
      postponed: [],
    }
    state.meetings.forEach((m) => {
      if (grouped[m.status]) {
        grouped[m.status].push(m)
      }
    })
    return grouped
  })

  const upcomingMeetings = computed(() => {
    const now = new Date()
    return state.meetings
      .filter((m) => m.status === 'scheduled' && new Date(m.scheduledDate) >= now)
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
  })

  const stats = computed(() => ({
    total: state.meetings.length,
    scheduled: state.meetings.filter((m) => m.status === 'scheduled').length,
    completed: state.meetings.filter((m) => m.status === 'completed').length,
    cancelled: state.meetings.filter((m) => m.status === 'cancelled').length,
  }))

  const getMeetingById = (id: string) =>
    computed(() => state.meetings.find((m) => m.id === id))

  const getMeetingsByProjectId = (projectId: string) =>
    computed(() => state.meetings.filter((m) => m.projectId === projectId))

  return {
    filteredMeetings,
    meetingsByStatus,
    upcomingMeetings,
    stats,
    getMeetingById,
    getMeetingsByProjectId,
  }
}
